import { useEffect, useRef } from 'react';

// Module-level map to track which tables have had initial hidden applied this page load
const initialHiddenAppliedForKey: Record<string, boolean> = {};

export interface UseInitialHiddenColumnsOptions {
  /** Table name/identifier (e.g., 'items-table') to track per-table application state */
  name?: string;
  /** Columns that should start hidden when they first appear (e.g., custom field columns). */
  initialHidden?: string[];
  /** Setter for column visibility from the table that owns the state (e.g. from useLocalStorageState). */
  setColumnVisibility: (
    updater: (prev: Record<string, boolean>) => Record<string, boolean>
  ) => void;
}

/**
 * Ensures initialHidden columns are hidden when they first appear (e.g. after async custom field definitions load).
 *
 * Call this from the component that owns column visibility state (e.g. OptimizedTable), passing its
 * setColumnVisibility so there is a single source of truth. Only sets visibility to false for columns
 * that have no persisted value yet, so user choices in localStorage are preserved across page refreshes.
 *
 * @example
 * ```tsx
 * const [columnVisibility, setColumnVisibility] = useLocalStorageState(...);
 * useInitialHiddenColumns({ name: 'items-table', initialHidden, setColumnVisibility });
 * ```
 */
export const useInitialHiddenColumns = ({
  name,
  initialHidden = [],
  setColumnVisibility,
}: UseInitialHiddenColumnsOptions): void => {
  const processedColumnsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (initialHidden.length === 0) return;

    // Determine if this is the first visit to this table this page load
    const isFirstVisitThisPageLoad = !name || !initialHiddenAppliedForKey[name];

    if (!isFirstVisitThisPageLoad) {
      // Already applied for this table this page load - but late-arriving columns (e.g. new custom
      // field definitions) may be missing from visibility; TanStack Table treats missing as visible.
      // Set to false any initialHidden id that is not yet in state so the table respects "all hidden".
      setColumnVisibility(prev => {
        const updates: Record<string, boolean> = {};
        for (const columnId of initialHidden) {
          if (columnId && prev[columnId] === undefined) {
            updates[columnId] = false;
          }
        }
        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
      });
      return;
    }

    // First visit to this table this page load - apply initial hide for columns without a persisted value.
    // Defer so we run after the current commit, but do NOT overwrite localStorage-backed user choices:
    // only columns with undefined visibility (i.e. never seen before) are hidden.
    queueMicrotask(() => {
      setColumnVisibility(prev => {
        const updates: Record<string, boolean> = {};
        let hasUpdates = false;

        for (const columnId of initialHidden) {
          if (columnId && !processedColumnsRef.current.has(columnId)) {
            // Mark as processed
            processedColumnsRef.current.add(columnId);
            // Hide only if no persisted value exists; preserve user toggles across refreshes.
            if (prev[columnId] === undefined) {
              updates[columnId] = false;
              hasUpdates = true;
            }
          }
        }

        return hasUpdates ? { ...prev, ...updates } : prev;
      });
    });

    // Mark this table as having had initial hidden applied this page load
    if (name) {
      initialHiddenAppliedForKey[name] = true;
    }
  }, [name, initialHidden, setColumnVisibility]);
};

/**
 * Reset the module-level tracking map. For testing purposes only.
 * @internal
 */
export const resetInitialHiddenTracking = (): void => {
  Object.keys(initialHiddenAppliedForKey).forEach(key => {
    delete initialHiddenAppliedForKey[key];
  });
};
